import React from 'react';

import {
  Box,
  Button,
  FormDateField,
  FormField,
  LoadingButton,
  PageHeader,
} from 'components';
import { useNotifications } from 'hooks';

import { FieldError, dayjs, useForm, useTranslation } from 'third-party';

import { APIApiClientTokenCreateModel } from 'types/api';

enum FormFields {
  Name = 'name',
  ExpiresAt = 'expiresAt',
}

type FormData = {
  [FormFields.Name]: string;
  [FormFields.ExpiresAt]: string | null;
};

type Props = {
  createPending: boolean;
  onSubmit: (model: APIApiClientTokenCreateModel) => Promise<any>;
  onClose: () => void;
};

export const CreateTokenModal: React.FC<Props> = ({
  createPending,
  onSubmit,
  onClose,
}) => {
  const { t } = useTranslation();
  const { error } = useNotifications();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [FormFields.Name]: '',
      [FormFields.ExpiresAt]: null,
    },
  });

  const onFormSubmit = async (formData: FormData) => {
    const result = await onSubmit({
      name: formData.name,
      expiresAt: formData.expiresAt
        ? dayjs(formData.expiresAt).format('YYYY-MM-DD')
        : undefined,
    });
    if (result?.error) {
      error(result.payload.body?.title || t('apiTokens.error.createFailed'));
    }
  };

  return (
    <Box padding="1rem 1rem 0 1rem">
      <PageHeader header={t('apiTokens.modal.create.title')}>
        <Box display="flex" gap="1rem">
          <LoadingButton
            variant="outlined"
            onClick={handleSubmit(onFormSubmit)}
            loading={createPending}
            disabled={createPending}
          >
            {t('apiTokens.modal.create.submit')}
          </LoadingButton>
          <Button
            variant="outlined"
            color="error"
            onClick={onClose}
            disabled={createPending}
          >
            {t('common.form.cancel')}
          </Button>
        </Box>
      </PageHeader>
      <form>
        <FormField
          fieldName={FormFields.Name}
          fieldError={errors[FormFields.Name] as FieldError}
          label={t('apiTokens.modal.create.name')}
          placeholder={
            t('common.form.enterValueFor') + t('apiTokens.modal.create.name')
          }
          requiredErrorMessage={t('common.form.fieldRequired')}
          control={control}
          maxLength={150}
          isRequired
          isDisabled={createPending}
        />
        <FormDateField
          fieldName={FormFields.ExpiresAt}
          fieldError={errors[FormFields.ExpiresAt] as FieldError}
          label={t('apiTokens.modal.create.expiresAt')}
          control={control}
          isRequired={false}
          isDisabled={createPending}
        />
      </form>
    </Box>
  );
};
